import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { batteryBrandsAPI } from '../../services/garageApi';
import { buildAssetUrl } from '../../utils/helpers';
import { Save, Battery, AlertCircle, CheckCircle } from 'lucide-react';
import BackButton from '../common/BackButton';

const EditBatteryBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: ''
  });
  const [existingImage, setExistingImage] = useState(null); // Existing image URL from backend
  const [newImage, setNewImage] = useState(null); // New image file selected by user
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  // Stable preview URL for the newly selected image File; revoked on change/unmount
  const previewUrl = useMemo(
    () => (newImage instanceof File ? URL.createObjectURL(newImage) : null),
    [newImage]
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fetchBrand = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await batteryBrandsAPI.show(id);
      console.log('Battery Brand API response:', response); // Debug log

      // batteryBrandsAPI.show() already unwraps via extractSingleDataFromResponse,
      // so response.data is the plain brand object.
      const brandData = response.data || null;

      console.log('Brand data:', brandData);

      if (brandData) {
        const brandName = brandData.name || '';
        console.log('🔍 EditBatteryBrand: Setting form data with name:', brandName);
        setFormData({
          name: brandName
        });
        
        // Set existing image if available
        if (brandData.image) {
          setExistingImage(brandData.image);
          console.log('🔍 EditBatteryBrand: Existing image found:', brandData.image);
        } else {
          setExistingImage(null);
        }
      } else {
        setError('Battery brand not found');
      }
    } catch (error) {
      console.error('Error fetching battery brand:', error);
      setError('Failed to load battery brand data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrors({});

    // Validate name before submitting
    if (!formData.name || !formData.name.trim()) {
      setErrors({ name: 'Brand name is required' });
      setError('Brand name is required');
      return;
    }

    setSaving(true);
    try {
      // Create FormData for multipart/form-data upload (including image)
      const submitData = new FormData();
      const nameValue = (formData.name || '').trim();
      
      // Ensure name is always sent as a non-empty string
      if (!nameValue) {
        setErrors({ name: 'Brand name is required' });
        setError('Brand name is required');
        setSaving(false);
        return;
      }
      
      submitData.append('name', nameValue);
      
      // Image file - only append if user selected a new one
      // If new image is provided, old image will be automatically deleted by backend
      if (newImage && newImage instanceof File) {
        submitData.append('image', newImage);
      }

      console.log('🔍 Submitting battery brand update with FormData...');
      console.log('🔍 Name value:', nameValue);
      console.log('🔍 FormData entries:');
      for (let pair of submitData.entries()) {
        console.log('  ', pair[1] instanceof File ? `File(${pair[1].name})` : pair[1]);
      }
      
      // Double-check name is in FormData
      const nameInFormData = submitData.get('name');
      console.log('🔍 Name in FormData (verification):', nameInFormData);

      await batteryBrandsAPI.update(id, submitData);
      
      navigate('/battery-brands', {
        state: { message: 'Battery brand updated successfully!' }
      });
    } catch (error) {
      console.error('Error updating battery brand:', error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const newErrors = {};
        Object.keys(validationErrors).forEach(field => {
          newErrors[field] = Array.isArray(validationErrors[field]) 
            ? validationErrors[field][0] 
            : validationErrors[field];
        });
        setErrors(newErrors);
        setError(Object.values(newErrors).join('\n'));
      } else {
        setError(error.response?.data?.message || 'Error updating battery brand. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !formData.name) {
    return (
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-700">{error}</div>
            <button
                onClick={fetchBrand}
                className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              Retry
            </button>
            <button
                onClick={() => navigate('/battery-brands')}
                className="mt-2 ml-2 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Back to Battery Brands
            </button>
          </div>
        </div>
    );
  }

  return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <BackButton to="/battery-brands" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Battery Brand</h1>
              <p className="text-sm text-gray-600 mt-1">Update battery brand information</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          {(error || errors.submit) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="whitespace-pre-line">{error || errors.submit}</span>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Battery className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Varta, Bosch, Exide"
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  required
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Image
              </label>
              
              {/* Existing Image Display */}
              {existingImage && !newImage && (
                <div className="mb-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Image</h4>
                  <div className="flex items-center gap-4">
                    <img 
                      src={buildAssetUrl(existingImage)}
                      alt="Current brand" 
                      className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'block';
                        }
                      }}
                    />
                    <div style={{ display: 'none' }} className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                      Image not found
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Current Image</p>
                      <p className="text-xs text-gray-500 mt-1">Upload a new image to replace this one</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* New Image Preview */}
              {newImage && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">New Image (will replace current)</h4>
                  <div className="flex items-center gap-4">
                    <img 
                      src={previewUrl}
                      alt="New brand preview"
                      className="w-32 h-32 object-cover rounded-lg border border-blue-300"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{newImage.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(newImage.size / 1024).toFixed(2)} KB
                      </p>
                      <button
                        type="button"
                        onClick={() => setNewImage(null)}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Image Upload Input */}
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg,image/webp"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    // Validate file size (5MB max)
                    if (file.size > 5120 * 1024) {
                      alert('Image size must be less than 5MB');
                      e.target.value = '';
                      return;
                    }
                    // Validate file type
                    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
                    if (!validTypes.includes(file.type)) {
                      alert('Invalid file type. Please upload a JPEG, PNG, or WEBP image.');
                      e.target.value = '';
                      return;
                    }
                    setNewImage(file);
                    // Clear error for this field
                    if (errors.image) {
                      setErrors(prev => ({ ...prev, image: '' }));
                    }
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Accepted formats: JPG, PNG, WEBP. Max size: 5MB
              </p>
              {errors.image && (
                <p className="mt-1 text-sm text-red-600">{errors.image}</p>
              )}
              {newImage && (
                <div className="mt-2 text-sm text-green-600 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  New image selected: {newImage.name}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/battery-brands')}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Update Brand
                </>
              )}
            </button>
          </div>
        </form>
      </div>
  );
};

export default EditBatteryBrand;